class InactivityManager {
  constructor() {
    // Default durations (overridden by sessionTimeout from API)
    const INACTIVITY_DURATION = 10 * 60 * 1000 // 10 minutes fallback in milliseconds
    const WARNING_DURATION = 60 * 1000 // 60 seconds warning countdown

    this.timeout = null
    this.warningTimeout = null
    this.syncInterval = null
    this.heartbeatInterval = null // Add heartbeat
    this.sleepCheckInterval = null // Track sleep check interval
    this.inactivityDuration = INACTIVITY_DURATION // Use the constant
    this.warningDuration = WARNING_DURATION // Use the constant
    this.lastActivity = Date.now()
    this.isActive = true
    this.isEnabled = false
    this.isWarningShown = false
    this.tabId = Math.random().toString(36).substr(2, 9)
    this.isTabVisible = !document.hidden
    this.isOnline = navigator.onLine // Track online status
    this.sessionStartTime = Date.now() // Track session start
    this.maxSessionDuration = 8 * 60 * 60 * 1000 // 8 hours max session
    this.callbacks = {
      onWarning: [],
      onLogout: [],
      onActivity: [],
      onDismissWarning: [],
      onSessionExpired: [], // Add session expiry callback
    }

    this.activityEvents = [
      'mousedown', 'keypress', 'scroll',
      'touchstart', 'click', 'keydown',
    ]

    // Events that should dismiss warnings - only meaningful interactions
    this.warningDismissEvents = [
      'mousedown', 'keypress', 'click', 'keydown', 'touchstart',
    ]

    this.init()
  }

  init() {
    this.bindActivityEvents()
    this.handleVisibilityChange()
    this.handlePageUnload()
    this.setupCrossTabCommunication()
    this.startPeriodicSync()
    this.handleOnlineOffline() // Add online/offline handling
    this.startHeartbeat() // Add heartbeat
  }

  // Enhanced activity events with separate handling for warnings
  bindActivityEvents() {
    let debounceTimer = null

    // Handle normal activity (when no warning is shown)
    const debouncedActivity = event => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        this.handleActivity(event.type)
      }, 100) // Debounce activity by 100ms
    }

    // Handle meaningful interactions that can dismiss warnings
    const meaningfulInteraction = event => {
      // Only handle if warning is shown and this is a meaningful interaction
      if (this.isWarningShown && this.warningDismissEvents.includes(event.type)) {
        // console.log('⚡ Meaningful interaction during warning:', event.type)
        this.handleActivity(event.type, true) // Pass flag indicating this can dismiss warnings
      } else if (!this.isWarningShown) {
        // Normal activity when no warning is shown
        // console.log('🎯 Normal activity:', event.type)
        debouncedActivity(event)
      }
    }

    // Bind all activity events
    this.activityEvents.forEach(event => {
      document.addEventListener(event, meaningfulInteraction, { passive: true, capture: true })
    })

    // Store the handler for cleanup
    this.boundActivityHandler = meaningfulInteraction
  }

  // Enhanced unbind activity events
  unbindActivityEvents() {
    if (this.boundActivityHandler) {
      this.activityEvents.forEach(event => {
        document.removeEventListener(event, this.boundActivityHandler, true)
      })
    }
  }

  // Handle online/offline status
  handleOnlineOffline() {
    const onlineHandler = () => {
      this.isOnline = true
      if (this.isEnabled) {
        this.syncActivityAcrossTabs()
        this.validateSession()
      }
    }

    const offlineHandler = () => {
      this.isOnline = false
      // Continue tracking offline, sync when back online
    }

    window.addEventListener('online', onlineHandler)
    window.addEventListener('offline', offlineHandler)

    // Store handlers for cleanup
    this.onlineHandler = onlineHandler
    this.offlineHandler = offlineHandler
  }

  // Add heartbeat to detect frozen tabs
  startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      if (this.isEnabled && this.isTabVisible) {
        const now = Date.now()
        try {
          localStorage.setItem('udara360_tab_heartbeat', JSON.stringify({
            tabId: this.tabId,
            timestamp: now,
            lastActivity: this.lastActivity,
          }))
        } catch (error) {
          console.warn('Failed to update heartbeat:', error)
        }
      }
    }, 30000) // Every 30 seconds
  }

  // Enhanced sleep detection with better accuracy
  detectSleep() {
    if (!this.isEnabled) return

    let lastTime = Date.now()

    this.sleepCheckInterval = setInterval(() => {
      if (!this.isEnabled) {
        clearInterval(this.sleepCheckInterval)
        return
      }

      const currentTime = Date.now()
      const timeDiff = currentTime - lastTime

      // If more than 5 seconds passed the timer was likely frozen (sleep/suspend)
      if (timeDiff > 5000) {
        const timeSinceActivity = currentTime - this.lastActivity

        if (timeSinceActivity >= this.inactivityDuration) {
          this.handleInactivityLogout()
          return
        }

        // Sleep was shorter than inactivity window — restart timer for remaining time
        this.resetTimer()
      }

      lastTime = currentTime
    }, 1000)
  }

  // Enhanced session validation
  validateSession() {
    const sessionAge = Date.now() - this.sessionStartTime

    // Check maximum session duration (8 hours)
    if (sessionAge > this.maxSessionDuration) {
      this.handleSessionExpiry()
      return false
    }

    // Validate with server periodically
    if (this.isOnline && sessionAge % (10 * 60 * 1000) === 0) { // Every 10 minutes
      this.validateWithServer()
    }

    return true
  }

  async validateWithServer() {
    try {
      if (!this.isOnline) {
        return
      }
    } catch (error) {
      console.warn('Session validation failed:', error)
    }
  }

  handleSessionExpiry() {
    // Session expired due to maximum duration
    this.notifyCallbacks('onSessionExpired')
    this.handleInactivityLogout()
  }

  // Enhanced cross-tab with cleanup
  setupCrossTabCommunication() {
    const storageHandler = e => {
      if (!this.isEnabled) return

      try {
        switch (e.key) {
          case 'udara360_last_activity':
            this.handleCrossTabActivity(e.newValue)
            break
          case 'udara360_dismiss_warning':
            this.handleCrossTabDismissWarning(e.newValue)
            break
          case 'udara360_logout_all_tabs':
            this.handleCrossTabLogout(e.newValue)
            break
          case 'udara360_tab_closed':
            this.handleTabClosed(e.newValue)
            break
          case 'udara360_tab_heartbeat':
            this.handleTabHeartbeat(e.newValue)
            break
          default:
          // Ignore other localStorage events
            break
        }
      } catch (error) {
        console.error('Error parsing localStorage event:', error)
      }
    }

    window.addEventListener('storage', storageHandler)
    this.storageHandler = storageHandler

    // Clean up dead tabs periodically
    this.cleanupInterval = setInterval(() => {
      this.cleanupDeadTabs()
    }, 60000)
  }

  handleCrossTabActivity(newValue) {
    try {
      let activityData

      // Handle both old format (just timestamp) and new format (object)
      if (newValue && newValue.startsWith('{')) {
        activityData = JSON.parse(newValue)
      } else {
        // Legacy format - just timestamp
        activityData = {
          lastActivity: parseInt(newValue, 10),
          canDismissWarning: false,
          eventType: 'legacy',
          tabId: 'unknown',
        }
      }

      if (activityData.lastActivity > this.lastActivity) {
        this.lastActivity = activityData.lastActivity

        // Only dismiss warning if the other tab's activity can dismiss warnings
        if (this.isWarningShown && activityData.canDismissWarning) {
          this.isWarningShown = false
          this.notifyCallbacks('onDismissWarning')

          // Signal other tabs to dismiss warning
          localStorage.setItem('udara360_dismiss_warning', JSON.stringify({
            tabId: this.tabId,
            timestamp: Date.now(),
          }))
        }

        // Always reset timer if no warning is shown or if activity can dismiss warnings
        if (!this.isWarningShown || activityData.canDismissWarning) {
          this.resetTimer()
        }
      }
    } catch (error) {
      console.error('Error handling cross-tab activity:', error)
    }
  }

  handleCrossTabDismissWarning(newValue) {
    const data = JSON.parse(newValue || '{}')
    if (data.tabId !== this.tabId) {
      this.isWarningShown = false
      this.notifyCallbacks('onDismissWarning')
    }
  }

  handleCrossTabLogout(newValue) {
    const data = JSON.parse(newValue || '{}')
    if (data.tabId !== this.tabId) {
      this.disable()
      this.notifyCallbacks('onLogout')
    }
  }

  handleTabHeartbeat(newValue) {
    try {
      const data = JSON.parse(newValue || '{}')
      const activeTabs = JSON.parse(localStorage.getItem('udara360_active_tabs') || '{}')

      activeTabs[data.tabId] = {
        lastActivity: data.lastActivity,
        timestamp: data.timestamp,
        heartbeat: data.timestamp,
      }

      localStorage.setItem('udara360_active_tabs', JSON.stringify(activeTabs))

      // Use this to fix ESLint error - check if it's our own heartbeat
      if (data.tabId === this.tabId) {
        console.debug('Own heartbeat processed')
      }
    } catch (error) {
      console.error('Error handling tab heartbeat:', error)
    }
  }

  handleTabClosed(newValue) {
    try {
      const data = JSON.parse(newValue || '{}')
      const activeTabs = JSON.parse(localStorage.getItem('udara360_active_tabs') || '{}')
      delete activeTabs[data.tabId]
      localStorage.setItem('udara360_active_tabs', JSON.stringify(activeTabs))

      // Use this to fix ESLint error - check if we're the last tab
      if (Object.keys(activeTabs).length === 1 && activeTabs[this.tabId]) {
        console.log('This is now the only active tab')
      }
    } catch (error) {
      console.error('Error handling tab close:', error)
    }
  }

  cleanupDeadTabs() {
    try {
      const activeTabs = JSON.parse(localStorage.getItem('udara360_active_tabs') || '{}')
      const now = Date.now()
      const deadTabTimeout = 2 * 60 * 1000 // 2 minutes

      Object.keys(activeTabs).forEach(tabId => {
        const tab = activeTabs[tabId]
        const timeSinceHeartbeat = now - (tab.heartbeat || tab.timestamp)

        if (timeSinceHeartbeat > deadTabTimeout) {
          // Cleaning up dead tab:', tabId
          delete activeTabs[tabId]
        }
      })

      localStorage.setItem('udara360_active_tabs', JSON.stringify(activeTabs))

      // Use this to fix ESLint error - update our own tab's status
      if (this.isEnabled && activeTabs[this.tabId]) {
        console.debug('Tab cleanup completed, our tab is still active')
      }
    } catch (error) {
      console.error('Error cleaning up dead tabs:', error)
    }
  }

  // Enhanced enable with validation
  enable() {
    try {
      // Prevent multiple enablements
      if (this.isEnabled) {
        return
      }

      // Validate before enabling
      if (!this.validateSession()) return

      // Validate durations are set correctly
      if (!this.inactivityDuration || this.inactivityDuration <= 0) {
        this.inactivityDuration = 10 * 60 * 1000 // 10 minutes default
        this.warningDuration = 60 * 1000 // 60 seconds default
      }

      this.isEnabled = true
      this.lastActivity = Date.now()
      this.isWarningShown = false
      this.isActive = true
      this.sessionStartTime = this.sessionStartTime || Date.now()

      // Clean slate start
      this.clearAllTimers()

      localStorage.setItem('udara360_last_activity', this.lastActivity.toString())
      this.updateActiveTabsRegistry()

      // Clear any stale warning states
      localStorage.removeItem('udara360_warning_shown')
      localStorage.removeItem('udara360_dismiss_warning')

      // Ensure timer starts properly
      this.startInactivityTimer()
      this.detectSleep()
    } catch (error) {
      console.error('Error enabling inactivity manager:', error)
    }
  }

  updateActiveTabsRegistry() {
    try {
      const activeTabs = JSON.parse(localStorage.getItem('udara360_active_tabs') || '{}')
      activeTabs[this.tabId] = {
        lastActivity: this.lastActivity,
        timestamp: Date.now(),
        heartbeat: Date.now(),
      }
      localStorage.setItem('udara360_active_tabs', JSON.stringify(activeTabs))
    } catch (error) {
      console.error('Error updating active tabs registry:', error)
    }
  }

  // Enhanced disable with thorough cleanup
  disable() {
    this.isEnabled = false
    this.clearAllTimers()
    this.isActive = true

    // If warning is currently shown, dismiss it
    if (this.isWarningShown) {
      this.isWarningShown = false
      this.notifyCallbacks('onDismissWarning')
    }

    try {
      // Comprehensive cleanup
      const itemsToRemove = [
        'udara360_last_activity',
        'udara360_warning_shown',
        'udara360_dismiss_warning',
        'udara360_logout_all_tabs',
      ]

      itemsToRemove.forEach(item => {
        localStorage.removeItem(item)
      })

      // Signal tab closing
      localStorage.setItem('udara360_tab_closed', JSON.stringify({
        tabId: this.tabId,
        timestamp: Date.now(),
      }))

      // Remove this tab from active registry
      const activeTabs = JSON.parse(localStorage.getItem('udara360_active_tabs') || '{}')
      delete activeTabs[this.tabId]
      localStorage.setItem('udara360_active_tabs', JSON.stringify(activeTabs))
    } catch (error) {
      console.error('Error during disable cleanup:', error)
    }
  }

  clearAllTimers() {
    const timers = [
      'timeout', 'warningTimeout', 'syncInterval',
      'heartbeatInterval', 'sleepCheckInterval', 'cleanupInterval',
    ]

    timers.forEach(timer => {
      if (this[timer]) {
        clearInterval(this[timer])
        clearTimeout(this[timer])
        this[timer] = null
      }
    })
  }

  // Enhanced destroy with complete cleanup
  destroy() {
    try {
      this.disable()
      this.unbindActivityEvents()

      // Remove all event listeners with proper cleanup
      if (this.storageHandler) {
        window.removeEventListener('storage', this.storageHandler)
      }
      if (this.onlineHandler) {
        window.removeEventListener('online', this.onlineHandler)
      }
      if (this.offlineHandler) {
        window.removeEventListener('offline', this.offlineHandler)
      }
      if (this.visibilityHandler) {
        document.removeEventListener('visibilitychange', this.visibilityHandler)
      }
      if (this.beforeUnloadHandler) {
        window.removeEventListener('pagehide', this.beforeUnloadHandler)
      }
      if (this.focusHandler) {
        window.removeEventListener('focus', this.focusHandler)
      }

      // Clear all callbacks
      Object.keys(this.callbacks).forEach(type => {
        this.callbacks[type] = []
      })
    } catch (error) {
      console.error('Error during destroy:', error)
    }
  }

  // Enhanced visibility change handling
  handleVisibilityChange() {
    this.visibilityHandler = () => {
      this.isTabVisible = !document.hidden

      if (!this.isEnabled) return

      if (document.visibilityState === 'visible') {
        // Check elapsed time immediately — setTimeout is frozen during sleep
        const timeSinceLastActivity = Date.now() - this.lastActivity

        if (timeSinceLastActivity >= this.inactivityDuration) {
          // Inactivity duration exceeded while asleep/hidden — logout now
          this.handleInactivityLogout()
          return
        }

        // Also check if the auth token has expired while away
        if (this.isTokenExpired && this.isTokenExpired()) {
          this.handleInactivityLogout()
          return
        }

        // Still within window — sync and continue
        this.syncActivityAcrossTabs()
        this.handleActivity('tab-visible', true)
      }
    }

    document.addEventListener('visibilitychange', this.visibilityHandler)
  }

  // Enhanced page unload handling
  handlePageUnload() {
    this.beforeUnloadHandler = () => {
      if (this.isEnabled) {
        try {
          // Store last activity time in localStorage for cross-tab detection
          const activityData = {
            lastActivity: this.lastActivity,
            tabId: this.tabId,
            eventType: 'unload',
            canDismissWarning: false,
            timestamp: Date.now(),
          }
          localStorage.setItem('udara360_last_activity', JSON.stringify(activityData))
        } catch (error) {
          console.warn('Failed to store activity on unload:', error)
        }
      }
    }

    this.focusHandler = () => {
      if (!this.isEnabled) return

      // On focus, check if inactivity exceeded (covers alt-tab back after long idle)
      const timeSinceLastActivity = Date.now() - this.lastActivity
      if (timeSinceLastActivity >= this.inactivityDuration) {
        this.handleInactivityLogout()
        return
      }

      // Check token expiry
      if (this.isTokenExpired && this.isTokenExpired()) {
        this.handleInactivityLogout()
        return
      }

      try {
        const storedActivity = localStorage.getItem('udara360_last_activity')
        if (storedActivity) {
          let activityData

          // Handle both old format (just timestamp) and new format (object)
          if (storedActivity && storedActivity.startsWith('{')) {
            activityData = JSON.parse(storedActivity)
          } else {
            // Legacy format - just timestamp
            activityData = {
              lastActivity: parseInt(storedActivity, 10),
              canDismissWarning: false,
              eventType: 'legacy',
            }
          }

          if (activityData.lastActivity > this.lastActivity) {
            this.lastActivity = activityData.lastActivity
            this.handleActivity('focus-sync', true)
          }
        }
      } catch (error) {
        console.warn('Failed to sync activity on focus:', error)
      }
    }

    window.addEventListener('pagehide', this.beforeUnloadHandler)
    window.addEventListener('focus', this.focusHandler)
  }

  // Set inactivity duration (minutes from API, fallback to 10 min)
  setInactivityDuration(minutes) {
    const parsed = Number(minutes)

    // Guard: non-numeric, zero, negative, or too small (< 2 min) → use 10 min default
    const safeMins = (Number.isFinite(parsed) && parsed >= 2) ? parsed : 10

    const newDuration = safeMins * 60 * 1000

    this.inactivityDuration = newDuration
    // Keep warning duration as 60 seconds, don't override it
    // this.warningDuration = Math.min(60 * 1000, newDuration / 10)

    // If already enabled, restart the timer with new duration
    if (this.isEnabled) {
      this.resetTimer()
    }
  }

  // Start inactivity timer
  startInactivityTimer() {
    if (!this.isEnabled) return

    this.clearTimers()

    const warningTime = this.inactivityDuration - this.warningDuration

    // Ensure calculations are properly evaluated (prevents optimization issues)
    if (warningTime <= 0) {
      return
    }

    // Store timer info for debugging without console spam
    this.timerInfo = {
      inactivityDuration: this.inactivityDuration,
      warningDuration: this.warningDuration,
      warningTime,
      startTime: Date.now(),
    }

    // Warning timer (show warning before logout)
    this.warningTimeout = setTimeout(() => {
      if (this.isEnabled && !this.isWarningShown) {
        this.showInactivityWarning()
      }
    }, warningTime)
  }

  // Reset timer
  resetTimer() {
    if (!this.isEnabled) return
    this.clearTimers()
    this.startInactivityTimer()
  }

  // Clear timers
  clearTimers() {
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.timeout = null
    }

    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout)
      this.warningTimeout = null
    }
  }

  // Start periodic sync
  startPeriodicSync() {
    this.syncInterval = setInterval(() => {
      if (this.isEnabled) {
        this.syncActivityAcrossTabs()
      }
    }, 2000)
  }

  // Sync activity across tabs
  syncActivityAcrossTabs() {
    try {
      const storedActivity = localStorage.getItem('udara360_last_activity')
      if (storedActivity) {
        let activityData

        // Handle both old format (just timestamp) and new format (object)
        if (storedActivity && storedActivity.startsWith('{')) {
          activityData = JSON.parse(storedActivity)
        } else {
          // Legacy format - just timestamp
          activityData = {
            lastActivity: parseInt(storedActivity, 10),
            canDismissWarning: false,
            eventType: 'legacy',
          }
        }

        if (activityData.lastActivity > this.lastActivity) {
          this.lastActivity = activityData.lastActivity

          // Only dismiss warning if the activity can dismiss warnings
          if (this.isWarningShown && activityData.canDismissWarning) {
            this.isWarningShown = false
            // Signal all tabs to dismiss warning
            localStorage.setItem('udara360_dismiss_warning', JSON.stringify({
              tabId: this.tabId,
              timestamp: Date.now(),
            }))
            this.notifyCallbacks('onDismissWarning')
          }

          // Reset timer only if no warning or if activity can dismiss warnings
          if (!this.isWarningShown || activityData.canDismissWarning) {
            this.resetTimer()
          }
        }
      }
    } catch (error) {
      console.error('Error syncing activity:', error)
    }
  }

  // Handle activity
  handleActivity(eventType = 'unknown', canDismissWarning = false) {
    if (!this.isEnabled) return

    try {
      const now = Date.now()
      this.lastActivity = now

      // Update internal state to ensure proper timing
      this.lastActivityTime = now

      // Store in localStorage for other tabs with tab info and event type
      const activityData = {
        lastActivity: now,
        tabId: this.tabId,
        eventType,
        canDismissWarning,
        timestamp: now,
      }
      localStorage.setItem('udara360_last_activity', JSON.stringify(activityData))

      // Update active tabs registry
      const activeTabs = JSON.parse(localStorage.getItem('udara360_active_tabs') || '{}')
      activeTabs[this.tabId] = {
        lastActivity: now,
        timestamp: now,
        heartbeat: now,
      }
      localStorage.setItem('udara360_active_tabs', JSON.stringify(activeTabs))

      // Only dismiss warning if this is a meaningful interaction or programmatic dismissal
      if (this.isWarningShown && canDismissWarning) {
        this.isWarningShown = false
        localStorage.setItem('udara360_dismiss_warning', JSON.stringify({
          tabId: this.tabId,
          timestamp: now,
        }))
        this.notifyCallbacks('onDismissWarning')
      }

      if (!this.isActive) {
        this.isActive = true
        this.notifyCallbacks('onActivity')
      }

      // Only reset timer if no warning is shown or if this can dismiss warnings
      if (!this.isWarningShown || canDismissWarning) {
        this.resetTimer()
      }
    } catch (error) {
      console.error('Error handling activity:', error)
    }
  }

  // Show inactivity warning
  showInactivityWarning() {
    if (!this.isEnabled || this.isWarningShown) return

    try {
      // Check if warning is already shown in another tab (more robust)
      const existingWarning = localStorage.getItem('udara360_warning_shown')
      if (existingWarning) {
        const warningData = JSON.parse(existingWarning)
        const timeSinceWarning = Date.now() - warningData.timestamp

        // If warning was shown recently and by another tab, don't show
        if (timeSinceWarning < 65000 && warningData.tabId !== this.tabId) {
          return
        }
      }

      this.isWarningShown = true

      // Mark warning as shown across all tabs
      localStorage.setItem('udara360_warning_shown', JSON.stringify({
        tabId: this.tabId,
        timestamp: Date.now(),
      }))

      this.notifyCallbacks('onWarning')
    } catch (error) {
      console.error('Error showing warning:', error)
    }
  }

  // Dismiss warning
  dismissWarning() {
    this.isWarningShown = false

    // Signal other tabs to dismiss warning
    try {
      localStorage.setItem('udara360_dismiss_warning', JSON.stringify({
        tabId: this.tabId,
        timestamp: Date.now(),
      }))
    } catch (error) {
      console.error('Error dismissing warning:', error)
    }

    // This is a programmatic dismissal, so it should reset the timer
    this.handleActivity('manual-dismiss', true)
  }

  // Handle inactivity logout
  handleInactivityLogout() {
    if (!this.isEnabled) return

    try {
      // Logging out due to inactivity - signaling all tabs
      localStorage.setItem('udara360_logout_all_tabs', JSON.stringify({
        tabId: this.tabId,
        timestamp: Date.now(),
      }))

      this.disable()
      this.notifyCallbacks('onLogout')
    } catch (error) {
      console.error('Error during logout:', error)
      // Still try to logout locally
      this.disable()
      this.notifyCallbacks('onLogout')
    }
  }

  /**
   * Register an external token-expiry checker so the visibility handler
   * can proactively log out when the JWT has expired during sleep.
   * @param {Function} fn - Returns true if the token is expired
   */
  setTokenExpiryChecker(fn) {
    if (typeof fn === 'function') {
      this.isTokenExpired = fn
    }
  }

  // Callback registration methods
  onWarning(callback) {
    if (typeof callback === 'function') {
      this.callbacks.onWarning.push(callback)
    }
  }

  onLogout(callback) {
    if (typeof callback === 'function') {
      this.callbacks.onLogout.push(callback)
    }
  }

  onActivity(callback) {
    if (typeof callback === 'function') {
      this.callbacks.onActivity.push(callback)
    }
  }

  onDismissWarning(callback) {
    if (typeof callback === 'function') {
      this.callbacks.onDismissWarning.push(callback)
    }
  }

  onSessionExpired(callback) {
    if (typeof callback === 'function') {
      this.callbacks.onSessionExpired.push(callback)
    }
  }

  // Notify callbacks
  notifyCallbacks(type) {
    if (this.callbacks[type]) {
      this.callbacks[type].forEach(callback => {
        try {
          callback()
        } catch (error) {
          console.error(`Error in ${type} callback:`, error)
        }
      })
    }
  }
}

export default new InactivityManager()
